import { useEffect, useRef, useState } from 'react'
import { noiseLight, smooth, type NoiseLight } from '@domain/rules/noise.ts'
import { startNoiseMeter, type NoiseFailure, type NoiseMeter } from '@adapters/audio/noiseMeter.ts'
import { es } from '../strings/es.ts'

/**
 * El semáforo de ruido, encendido.
 *
 * Es la única pantalla de la app que no es de nadie: la tablet se deja en la
 * mesa y la miran los dos. Eso tiene una consecuencia que decide el diseño
 * entero — **el micrófono oye la habitación, no a una persona**. No puede saber
 * quién ha gritado, así que cualquier consecuencia que colgara de él sería un
 * castigo colectivo, y además el más injusto de todos: el hermano callado
 * pagaría por el otro.
 *
 * Por eso esto informa y no puntúa. No da monedas, no las quita, y no escribe
 * una línea en el registro. Es un espejo, no un juez, y la app entera está
 * construida sobre la regla de que nunca se resta.
 *
 * Lo demás sale de lo que ya sabemos que le hace falta al hijo que necesita
 * previsibilidad: se ve cuánto queda desde el primer segundo, el color dice qué
 * pasa con una frase literal y sin «casi», y no hay ningún sonido — un pitido
 * para avisar de que hay ruido sería, además de irónico, justo lo que peor
 * sienta.
 */
export function Semaforo({
  minutes,
  limit,
  reducedMotion,
  onDone,
}: {
  minutes: number
  limit: number
  reducedMotion: boolean
  onDone: () => void
}) {
  const [light, setLight] = useState<NoiseLight>('verde')
  const [level, setLevel] = useState(0)
  const [failure, setFailure] = useState<NoiseFailure | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(minutes * 60)
  const nivel = useRef(0)
  const color = useRef<NoiseLight>('verde')

  useEffect(() => {
    let meter: NoiseMeter | null = null
    let cancelled = false

    void (async () => {
      const started = await startNoiseMeter((sample) => {
        nivel.current = smooth(nivel.current, sample)
        color.current = noiseLight(nivel.current, limit, color.current)
        setLevel(nivel.current)
        setLight(color.current)
      })
      if (cancelled) {
        if (started.ok) started.meter.stop()
        return
      }
      if (started.ok) meter = started.meter
      else setFailure(started.failure)
    })()

    return () => {
      cancelled = true
      meter?.stop()
    }
  }, [limit])

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => {
      clearInterval(id)
    }
  }, [])

  const over = secondsLeft <= 0
  const minutesLeft = Math.floor(secondsLeft / 60)
  const rest = String(secondsLeft % 60).padStart(2, '0')

  const tone: Record<NoiseLight, { bg: string; fg: string; label: string; help: string }> = {
    verde: {
      bg: 'bg-mint-soft',
      fg: 'text-mint',
      label: es.semaforo.verde,
      help: es.semaforo.verdeHelp,
    },
    ambar: {
      bg: 'bg-gold-soft',
      fg: 'text-gold',
      label: es.semaforo.ambar,
      help: es.semaforo.ambarHelp,
    },
    rojo: {
      bg: 'bg-coral-soft',
      fg: 'text-coral',
      label: es.semaforo.rojo,
      help: es.semaforo.rojoHelp,
    },
  }
  const now = tone[light]

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center px-5 py-8">
      <h1 className="text-muted mb-6 text-center text-xs font-bold tracking-[0.12em] uppercase">
        {es.semaforo.title}
      </h1>

      {failure ? (
        <p role="alert" className="text-coral mb-6 text-center text-lg font-bold">
          {failure === 'sin-permiso' ? es.semaforo.noPermission : es.semaforo.noMic}
        </p>
      ) : (
        <section className={`${now.bg} mb-6 rounded-3xl p-10 text-center`}>
          <p
            aria-live="polite"
            className={`${now.fg} m-0 text-4xl leading-tight font-extrabold tracking-tight`}
          >
            {now.label}
          </p>
          <p className="m-0 mt-3 text-lg font-bold">{now.help}</p>

          <div
            className="border-rule bg-surface relative mt-8 h-6 overflow-hidden rounded-full border-2"
            role="img"
            aria-label={es.semaforo.now}
          >
            <div
              className={`absolute inset-y-0 left-0 ${light === 'rojo' ? 'bg-coral' : light === 'ambar' ? 'bg-gold' : 'bg-mint'}`}
              style={{
                width: `${String(Math.round(Math.min(1, level) * 100))}%`,
                transition: reducedMotion ? 'none' : 'width .12s linear',
              }}
            />
            <div
              className="bg-ink absolute inset-y-0 w-1"
              style={{ left: `${String(Math.round(Math.min(1, limit) * 100))}%` }}
            />
          </div>
        </section>
      )}

      <p className="mb-8 text-center text-2xl font-extrabold" aria-live="off">
        {over
          ? es.semaforo.done
          : minutesLeft === 0
            ? es.semaforo.lastMinute(rest)
            : es.semaforo.left(minutesLeft, rest)}
      </p>

      <p className="text-muted mx-auto mb-6 max-w-sm text-center text-sm">
        {es.semaforo.neverPunishes}
      </p>

      <button
        type="button"
        onClick={onDone}
        className="border-rule mx-auto rounded-xl border-2 px-8 py-4 text-lg font-bold"
      >
        {es.semaforo.stop}
      </button>
    </div>
  )
}
